import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useMaterialReservation } from './MaterialReservationContext';
import { Typography, CircularProgress, Button, Grid, Paper, TextField, Snackbar, Skeleton, Box, Stack, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Checkbox, FormControlLabel, Tooltip, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import GereserveerdMateriaalLijst from './GereserveerdMateriaalLijst';
import SimpleMaterialWizard from './SimpleMaterialWizard';
import ReserveringsData from './ReserveringsData';
import { toast } from 'react-hot-toast';
import CopyMaterialsDialog from './CopyMaterialsDialog';

export const MaterialenOverzicht = React.memo(function MaterialenOverzicht({ updateDraaidagLocally, fetchDraaidagenFromBackend }) {
    const {
        selectedDraaidag,
        setSelectedDraaidag,
        reservations,
        fetchReservations,
        setCurrentReservation,
        currentReservation,
        loading: reservationsLoading,
        error,
        updateReservationField
    } = useMaterialReservation();

    // Memoize the current reservation lookup (NU BOVENAAN)
    const currentReservationMemo = useMemo(() => {
        return currentReservation;
    }, [currentReservation]);

    const [alleMaterialen, setAlleMaterialen] = useState([]);
    const [isMaterialsLoading, setIsMaterialsLoading] = useState(false);
    const [materialsCache, setMaterialsCache] = useState({ data: null, timestamp: null });
    const MATERIALS_CACHE_DURATION = 5 * 60 * 1000; // 5 minuten cache
    const [openWizard, setOpenWizard] = useState(false);
    const [selectedMaterials, setSelectedMaterials] = useState([]);
    const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
    const [notitie, setNotitie] = useState('');
    const [notitieStatus, setNotitieStatus] = useState('');
    const [isWaitingForNotitieSync, setIsWaitingForNotitieSync] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const debounceRef = useRef();
    const saveTimeoutRef = useRef();
    const lastSavedValue = useRef('');
    const [geenMateriaalNodig, setGeenMateriaalNodig] = useState(false);

    // Update geenMateriaalNodig when selectedDraaidag changes
    useEffect(() => {
        if (selectedDraaidag?.fields) {
            const isGeenMateriaalNodig = selectedDraaidag.fields['Geen Materiaal Nodig'] || false;
            setGeenMateriaalNodig(isGeenMateriaalNodig);
        }
    }, [selectedDraaidag?.id, selectedDraaidag?.fields?.['Geen Materiaal Nodig']]);
    const [opslaanStatus, setOpslaanStatus] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    const [materiaalsets, setMateriaalsets] = useState([]);
    const [saveAsSetDialogOpen, setSaveAsSetDialogOpen] = useState(false);
    const [newSetName, setNewSetName] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [extraDagNa, setExtraDagNa] = useState(false);
    const [extraDagVoor, setExtraDagVoor] = useState(false);

    // Resolve material ID from OLD base via backend (security: API key not exposed)
    const resolveOldBaseMaterialRecordId = useCallback(async (material) => {
        try {
            const materialId = material?.fields?.ID || '';
            const materialName = material?.fields?.Naam || '';

            if (!materialId && !materialName) return null;

            const params = new URLSearchParams();
            if (materialId) params.append('materialId', materialId);
            if (materialName) params.append('materialName', materialName);

            const response = await axios.get(
                `${process.env.REACT_APP_CURRENT_URL}/Api/materialen/resolve-old-base?${params.toString()}`
            );
            
            return response.data?.recordId || null;
        } catch (e) {
            console.error('resolveOldBaseMaterialRecordId failed:', e.response?.data || e.message);
            return null;
        }
    }, []);

    // Use a Set for faster lookups
    const selectedMaterialsSet = useMemo(() => {
        return new Set(selectedMaterials.map(m => m.id));
    }, [selectedMaterials]);

    // Airtable base ID voor links
    const AIRTABLE_BASE_ID = 'apphMT5j6gtUnMY1p';
    const AIRTABLE_PROJECT_PAGE = 'pagiiQRyyfYj7RHbl';
    const AIRTABLE_CALLSHEET_PAGE = 'pagEOROTuaYMwBBKi';
    const AIRTABLE_CALLSHEET_TABLE = 'tblsyhoYyJOGZiW1T';

    // Optimistic preview van draaidag data
    const optimisticDraaidagData = useMemo(() => {
        if (!selectedDraaidag) return null;
        
        const fields = selectedDraaidag.fields || {};
        const originalTitle = fields.Draaidag || '';
        
        // Haal projectnummer uit de titel als ProjectNumRegex niet beschikbaar is
        const projectNumFromTitle = originalTitle.match(/\[(\d+)\]/)?.[1] || null;
        
        // Haal projectnaam uit titel (tussen || en [nummer])
        const projectNaamFromTitle = originalTitle.match(/\|\|\s*([^-\[]+)/)?.[1]?.trim() || '';
        
        // Crew: probeer eerst Camerapersonen (tekst), dan Crew lookup naam
        let rawCrewNaam = fields.Camerapersonen || 
                         fields['Crew (from Project)'] || 
                         fields.CrewNaam ||
                         (typeof fields.Crew === 'string' ? fields.Crew : '') ||
                         '';
        
        // Format crew namen: als het een array is, join met " + "
        // Als het een string is zonder separators, probeer te splitsen op hoofdletters
        let crewNaam = '';
        if (Array.isArray(rawCrewNaam)) {
            crewNaam = rawCrewNaam.join(' + ');
        } else if (typeof rawCrewNaam === 'string') {
            // Check of het al separators heeft
            if (rawCrewNaam.includes('+') || rawCrewNaam.includes(',') || rawCrewNaam.includes(' en ')) {
                crewNaam = rawCrewNaam;
            } else {
                // Probeer te splitsen op hoofdletters (bijv. "TonRobin" -> "Ton + Robin")
                // Maar alleen als er meerdere hoofdletters zijn na de eerste
                const splitNames = rawCrewNaam.split(/(?=[A-Z])/).filter(n => n.length > 0);
                if (splitNames.length > 1 && splitNames.every(n => n.length >= 2)) {
                    crewNaam = splitNames.join(' + ');
                } else {
                    crewNaam = rawCrewNaam;
                }
            }
        }
        
        // Project: probeer lookup naam velden
        const projectNaam = fields['ProjectNaam (from Project)'] ||
                           fields.ProjectNaam ||
                           fields['Naam (from Project)'] ||
                           projectNaamFromTitle ||
                           '';
        
        // Project record ID voor link
        const projectRecordId = Array.isArray(fields.Project) ? fields.Project[0] : fields.Project || null;
        
        // Callsheet: check of er een callsheet is gekoppeld
        // Probeer verschillende veldnamen voor de callsheet linked record
        const callsheetRecordId = Array.isArray(fields.Callsheet) ? fields.Callsheet[0] : 
                                  Array.isArray(fields['Callsheets']) ? fields['Callsheets'][0] :
                                  Array.isArray(fields['CallSheet']) ? fields['CallSheet'][0] : null;
        
        
        // PDF veld kan attachment zijn (array met url) of lookup
        // Probeer verschillende veldnamen - "Callsheets PDFs" is de correcte naam
        const pdfField = fields['Callsheets PDFs'] ||
                        fields['PDF Callsheet'] || 
                        fields['PDF (from Callsheet)'] || 
                        fields['PDF (from CallSheet)'] ||
                        fields['CallsheetPDF'] ||
                        fields['Callsheet PDF'] ||
                        fields['PDF'] ||
                        null;
        
        // Als het een attachment is, haal de URL eruit
        let callsheetPdfUrl = null;
        if (pdfField) {
            if (Array.isArray(pdfField) && pdfField.length > 0) {
                // Attachment veld: [{url: '...', filename: '...'}]
                callsheetPdfUrl = pdfField[0]?.url || pdfField[0];
            } else if (typeof pdfField === 'string') {
                callsheetPdfUrl = pdfField;
            }
        }
        
        return {
            id: selectedDraaidag.id,
            title: originalTitle,
            weergaveNaam: fields.WeergaveNaamMateriaalApp || '',
            displayTitle: fields.WeergaveNaamMateriaalApp || originalTitle || 'Onbekende draaidag',
            startDate: fields.StartDatum,
            endDate: fields.EindDatum,
            projectType: fields.ProjectType?.[0] || fields['Projecttype (from Project)']?.[0] || 'Onbekend',
            geenMateriaalNodig: fields['Geen Materiaal Nodig'] || false,
            notities: currentReservation?.fields?.Notities || '',
            // Extra velden
            crew: crewNaam,
            projectNummer: fields.ProjectNumRegex || projectNumFromTitle || '',
            projectNaam: projectNaam,
            projectRecordId: projectRecordId,
            aantalDagen: fields['Aantal dagen'] || fields.Dagen || '',
            duurUren: fields.DuurUren || '',
            // Callsheet info
            callsheetRecordId: callsheetRecordId,
            callsheetPdfUrl: callsheetPdfUrl
        };
    }, [selectedDraaidag, currentReservation]);

    // State voor editable weergavenaam
    const [editWeergaveNaam, setEditWeergaveNaam] = useState('');
    const [isEditingNaam, setIsEditingNaam] = useState(false);
    
    // Sync weergaveNaam wanneer draaidag wijzigt
    useEffect(() => {
        if (selectedDraaidag) {
            setEditWeergaveNaam(selectedDraaidag.fields?.WeergaveNaamMateriaalApp || '');
        }
    }, [selectedDraaidag]);

    // Handler voor opslaan weergavenaam
    const handleSaveWeergaveNaam = useCallback(async () => {
        if (!selectedDraaidag?.id) return;
        
        // Check of de waarde daadwerkelijk veranderd is
        const currentValue = selectedDraaidag.fields?.WeergaveNaamMateriaalApp || '';
        const newValue = editWeergaveNaam || '';
        
        if (currentValue === newValue) {
            // Geen wijziging, alleen editing mode sluiten
            setIsEditingNaam(false);
            return;
        }
        
        try {
            await axios.patch(`${process.env.REACT_APP_CURRENT_URL}/Api/draaidagen/${selectedDraaidag.id}`, {
                fields: {
                    WeergaveNaamMateriaalApp: editWeergaveNaam || null // null om te wissen
                }
            });
            
            // Update lokale state
            const updatedDraaidag = {
                ...selectedDraaidag,
                fields: {
                    ...selectedDraaidag.fields,
                    WeergaveNaamMateriaalApp: editWeergaveNaam || null
                }
            };
            setSelectedDraaidag(updatedDraaidag);
            setIsEditingNaam(false);
            toast.success(editWeergaveNaam ? 'Weergavenaam opgeslagen!' : 'Weergavenaam gewist (originele naam wordt getoond)');
        } catch (error) {
            console.error('Fout bij opslaan weergavenaam:', error);
            toast.error('Fout bij opslaan weergavenaam');
        }
    }, [selectedDraaidag, editWeergaveNaam, setSelectedDraaidag]);

    // Parallel data fetching voor snellere laadtijden
    const loadAllDataParallel = useCallback(async (showLoading = true) => {
        if (!selectedDraaidag) return;
        
        if (showLoading) {
            setIsSaving(true);
        }
        
        try {
            const reservationId = currentReservation?.id;
            const url = reservationId 
                ? `${process.env.REACT_APP_CURRENT_URL}/Api/draaidag-data/${selectedDraaidag.id}?reservationId=${reservationId}`
                : `${process.env.REACT_APP_CURRENT_URL}/Api/draaidag-data/${selectedDraaidag.id}`;
            
            const response = await axios.get(url);
            
            const { materials, junctionData } = response.data;
            const realJunctions = Array.isArray(junctionData)
                ? junctionData.filter(j => j && j.id && !String(j.id).startsWith('synthetic_'))
                : [];

            // Build fast lookup map: materialId -> { junctionId, statuses }
            const junctionByMaterialId = new Map();
            for (const j of realJunctions) {
                const matId = Array.isArray(j?.fields?.Materiaal) ? j.fields.Materiaal[0] : null;
                if (matId) {
                    junctionByMaterialId.set(matId, { id: j.id, statuses: j.fields?.StatusJunction || [] });
                }
            }
            setAlleMaterialen(materials);

            if (currentReservation && materials.length > 0) {
                // Check for legacy reservation (text-based material linking)
                const legacyJunction = junctionData.find(j => j._legacyReservation);
                
                let initiallySelected = [];
                
                if (legacyJunction) {
                    // Handle legacy reservation with text-based material names
                    const materialNames = legacyJunction._materialNames || [];
                    
                    // Match material names to material IDs
                    initiallySelected = materials.filter(material => {
                        const materialName = material.fields?.ID || material.fields?.Naam || '';
                        return materialNames.some(name => 
                            materialName.toLowerCase().includes(name.toLowerCase()) ||
                            name.toLowerCase().includes(materialName.toLowerCase())
                        );
                    });
                } else {
                    // Prefer REAL junctionData as the source of truth
                    const reservedIdsFromJunction = new Set(
                        realJunctions
                            .filter(j => Array.isArray(j.fields?.Materiaal))
                            .map(j => j.fields.Materiaal[0])
                    );

                    if (reservedIdsFromJunction.size > 0) {
                        initiallySelected = materials.filter(m => reservedIdsFromJunction.has(m.id));
                    } else {
                        // Fallback to legacy Materiaal field on reservation (array of IDs)
                        const reservedIds = new Set(currentReservation.fields?.Materiaal || []);
                        initiallySelected = materials.filter(m => reservedIds.has(m.id));
                    }
                }


                // Add junction data to materials using the map
                const materialsWithJunction = initiallySelected.map(material => {
                    const j = junctionByMaterialId.get(material.id);
                    if (j) {
                        return {
                            ...material,
                            fields: {
                                ...material.fields,
                                JunctionID: j.id,
                                JunctionStatus: j.statuses
                            }
                        };
                    }
                    return material;
                });

                setSelectedMaterials(materialsWithJunction);

                // Also update alleMaterialen with junction data for consistency
                const updatedAlleMaterialen = materials.map(material => {
                    const j = junctionByMaterialId.get(material.id);
                    if (j) {
                        return {
                            ...material,
                            fields: {
                                ...material.fields,
                                JunctionID: j.id,
                                JunctionStatus: j.statuses
                            }
                        };
                    }
                    return material;
                });
                setAlleMaterialen(updatedAlleMaterialen);
            } else {
                setSelectedMaterials([]);
            }
    
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Fout bij ophalen van data');
        } finally {
            if (showLoading) {
                setIsSaving(false);
            }
        }
    }, [selectedDraaidag, currentReservation]);

    // Fetch materiaalsets
    const fetchMateriaalsets = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_CURRENT_URL}/Api/materiaalsets`);
            setMateriaalsets(response.data);
        } catch (error) {
            console.error('Error fetching materiaalsets:', error);
            toast.error('Fout bij ophalen van materiaalsets');
        }
    }, []);

    // Separate refresh function that doesn't depend on currentReservation
    const refreshMaterialData = useCallback(async () => {
        if (!selectedDraaidag) return;
        
        
        setIsSaving(true);
        
        try {
            // Fetch fresh data without reservation context
            const url = `${process.env.REACT_APP_CURRENT_URL}/Api/draaidag-data/${selectedDraaidag.id}`;
            
            const response = await axios.get(url);
            const { materials, junctionData } = response.data;
            
            
            // Update materials without any reservation context
            setAlleMaterialen(materials);
            setSelectedMaterials([]); // Clear selected materials since reservation was deleted
            
        } catch (error) {
            console.error('Error refreshing material data:', error);
            toast.error('Fout bij verversen van materialen data');
        } finally {
            setIsSaving(false);
        }
    }, [selectedDraaidag]);

    // Vervang de oude useEffect met de nieuwe parallel loading
    useEffect(() => {
        loadAllDataParallel();
    }, [loadAllDataParallel]);

    // Auto-set currentReservation in context when draaidag is selected
    useEffect(() => {
        if (selectedDraaidag && Array.isArray(reservations)) {
            const normalize = (d) => {
                if (!d) return '';
                const dt = new Date(d);
                if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
                return dt.toISOString().slice(0, 10);
            };

            const selId = selectedDraaidag.id;
            // Strict policy: only match via link field IDdraaidagOutlook
            const found = reservations.find(r =>
                Array.isArray(r?.fields?.IDdraaidagOutlook) && r.fields.IDdraaidagOutlook.includes(selId)
            );

            
            // ALWAYS update currentReservation when draaidag changes, regardless of current state
            if (found) {
                setCurrentReservation(found);
            } else {
                setCurrentReservation(null);
            }
        }
    }, [selectedDraaidag?.id, reservations, setCurrentReservation]); // Remove currentReservation from dependencies to prevent loops

    // Force refresh when currentReservation changes (e.g., after saving new materials)
    useEffect(() => {
        if (selectedDraaidag && currentReservation) {
            // Use setTimeout to ensure the currentReservation is fully updated
            // Don't set loading state for this refresh to avoid loader staying on screen
            setTimeout(() => {
                loadAllDataParallel(false); // false = geen loading state
            }, 100);
        }
    }, [currentReservation?.id]); // Only trigger when the reservation ID changes

    // Fetch materiaalsets on mount
    useEffect(() => {
        fetchMateriaalsets();
    }, [fetchMateriaalsets]);

    // Optimistic UI component
    const DraaidagPreview = () => {
        if (!optimisticDraaidagData) return null;
        
        return (
            <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                {/* Editable titel sectie */}
                {isEditingNaam ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <TextField
                            size="small"
                            value={editWeergaveNaam}
                            onChange={(e) => setEditWeergaveNaam(e.target.value)}
                            placeholder={optimisticDraaidagData.title}
                            label="Weergavenaam (laat leeg voor origineel)"
                            fullWidth
                            autoFocus
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleSaveWeergaveNaam();
                                }
                            }}
                            onBlur={() => {
                                // Auto-save wanneer je uit het veld klikt
                                handleSaveWeergaveNaam();
                            }}
                        />
                        <Tooltip title="Opslaan" arrow>
                            <IconButton color="primary" onClick={handleSaveWeergaveNaam} size="small">
                                <CheckIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Annuleren (wijzigingen niet opslaan)" arrow>
                            <IconButton onClick={() => {
                                setIsEditingNaam(false);
                                setEditWeergaveNaam(selectedDraaidag?.fields?.WeergaveNaamMateriaalApp || '');
                            }} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ) : (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="h6">
                            {optimisticDraaidagData.displayTitle}
                        </Typography>
                        <Tooltip title="Klik om weergavenaam aan te passen" arrow>
                            <IconButton onClick={() => setIsEditingNaam(true)} size="small" color="primary">
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {optimisticDraaidagData.weergaveNaam && (
                            <Typography variant="caption" color="text.secondary">
                                (origineel: {optimisticDraaidagData.title})
                            </Typography>
                        )}
                    </Stack>
                )}
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>📅 Datum:</strong> {optimisticDraaidagData.startDate}
                            {optimisticDraaidagData.endDate !== optimisticDraaidagData.startDate && 
                                ` - ${optimisticDraaidagData.endDate}`}
                            {optimisticDraaidagData.aantalDagen && ` (${optimisticDraaidagData.aantalDagen})`}
                        </Typography>
                        {optimisticDraaidagData.duurUren && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>⏱️ Duur:</strong> {optimisticDraaidagData.duurUren} uur
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        {optimisticDraaidagData.crew && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>👥 Crew:</strong> {optimisticDraaidagData.crew}
                            </Typography>
                        )}
                        {optimisticDraaidagData.projectNummer && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>📋 Project:</strong>{' '}
                                {optimisticDraaidagData.projectRecordId ? (
                                    <a 
                                        href={`https://airtable.com/${AIRTABLE_BASE_ID}/${AIRTABLE_PROJECT_PAGE}/${optimisticDraaidagData.projectRecordId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#1976d2', textDecoration: 'none' }}
                                    >
                                        [{optimisticDraaidagData.projectNummer}] {optimisticDraaidagData.projectNaam && `- ${optimisticDraaidagData.projectNaam}`} 🔗
                                    </a>
                                ) : (
                                    <>
                                        [{optimisticDraaidagData.projectNummer}]
                                        {optimisticDraaidagData.projectNaam && ` - ${optimisticDraaidagData.projectNaam}`}
                                    </>
                                )}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>🎬 Type:</strong> {optimisticDraaidagData.projectType}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>📦 Materiaal:</strong> {optimisticDraaidagData.geenMateriaalNodig ? 'Niet nodig' : 'Nodig'}
                        </Typography>
                        {/* Callsheet link */}
                        {(optimisticDraaidagData.callsheetRecordId || optimisticDraaidagData.callsheetPdfUrl) && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>📄 Callsheet:</strong>{' '}
                                {optimisticDraaidagData.callsheetPdfUrl ? (
                                    <a 
                                        href={optimisticDraaidagData.callsheetPdfUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#1976d2', textDecoration: 'none' }}
                                    >
                                        Open PDF 🔗
                                    </a>
                                ) : optimisticDraaidagData.callsheetRecordId ? (
                                    <a 
                                        href={`https://airtable.com/${AIRTABLE_BASE_ID}/${AIRTABLE_CALLSHEET_PAGE}/${optimisticDraaidagData.callsheetRecordId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#1976d2', textDecoration: 'none' }}
                                    >
                                        Bekijken 🔗
                                    </a>
                                ) : null}
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        );
    };

    // Process reservations data for availability checking
    const processReservationsData = useCallback((reservations) => {
            const reservationsData = {};

        if (Array.isArray(reservations)) {
            reservations.forEach(record => {
                if (record.fields && record.fields.Materiaal && Array.isArray(record.fields.Materiaal)) {
                    // Get the date range for this reservation
                    const startDate = new Date(record.fields.Start);
                    const endDate = new Date(record.fields.Eind);
                    const draaidagNaam = record.fields.DraaidagNaam?.[0] || record.fields.DraaidagNaamOutlook?.[0] || 'Onbekende draaidag';
                    
                    record.fields.Materiaal.forEach(materialId => {
                        if (!reservationsData[materialId]) {
                            reservationsData[materialId] = [];
                        }
                        
                        // Store reservation info with draaidag name
                        reservationsData[materialId].push({
                            startDate: startDate.toISOString(),
                            endDate: endDate.toISOString(),
                            draaidagNaam: draaidagNaam,
                            reservationId: record.id
                        });
                    });
                }
            });
        }

        return reservationsData;
    }, []);

    // Add updateJunctionStatus function
    const updateJunctionStatus = useCallback(async (junctionId, statuses) => {
        if (!junctionId) {
            console.error('JunctionID is undefined - cannot update status');
            toast.error('Kan status niet updaten - JunctionID ontbreekt. Probeer de pagina te verversen.');
            return { ok: false, error: 'JunctionID is undefined' };
        }
        
        try {
            const response = await axios.patch(`${process.env.REACT_APP_CURRENT_URL}/Api/junctions/${junctionId}`, {
                statuses: Array.isArray(statuses) ? statuses : [statuses]
            });
            
            return { ok: true, data: response.data };
        } catch (error) {
            console.error('Error updating junction status:', error);
            if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
            }
            toast.error('Fout bij het updaten van de status');
            return { ok: false, error };
        }
    }, []);

    // Add updateJunctionStatus function with legacy support
    const updateJunctionStatusWithLegacySupport = useCallback(async (material, statuses) => {
        // Legacy/geen junction: altijd via backend een echte junction zoeken of maken
        const apiBase = process.env.REACT_APP_CURRENT_URL;
        const statusesArray = Array.isArray(statuses) ? statuses : [statuses];
        const isSynthetic = !material.fields?.JunctionID || String(material.fields?.JunctionID).startsWith('synthetic_');
        if (isSynthetic) {
            try {
                // Check bestaande junction
                const existingResp = await axios.get(`${apiBase}/Api/junctions/existing`, {
                    params: { reservationId: currentReservation?.id, materialId: material.id }
                });
                let junctionId = existingResp.data?.[0]?.id;
                if (!junctionId) {
                    // Maak echte junction aan in oude base
                    const createResp = await axios.post(`${apiBase}/Api/junctions`, {
                        reservationId: currentReservation?.id,
                        materialId: material.id,
                        statuses: statusesArray,
                        draaidagId: selectedDraaidag?.id
                    });
                    junctionId = createResp.data?.records?.[0]?.id;
                } else {
                    // Update bestaande junction
                    await axios.patch(`${apiBase}/Api/junctions/${junctionId}`, { statuses: statusesArray });
                }

                const updatedMaterial = {
                    ...material,
                    fields: {
                        ...material.fields,
                        JunctionID: junctionId,
                        JunctionStatus: statusesArray
                    }
                };
                setSelectedMaterials(prev => prev.map(m => m.id === material.id ? updatedMaterial : m));
                setAlleMaterialen(prev => prev.map(m => m.id === material.id ? updatedMaterial : m));
                toast.success('Status bijgewerkt');
                return { ok: true };
            } catch (e) {
                console.error('Backend junction create/update failed:', e.response?.data || e.message);
                toast.error('Fout bij updaten van status');
                return { ok: false, error: e };
            }
        }
        
        // Normal junction update for non-legacy materials
        try {
            const response = await axios.patch(`${process.env.REACT_APP_CURRENT_URL}/Api/junctions/${material.fields.JunctionID}`, {
                statuses: Array.isArray(statuses) ? statuses : [statuses]
            });
            
            return { ok: true, data: response.data };
        } catch (error) {
            console.error('Error updating junction status:', error);
            toast.error('Fout bij het updaten van de status');
            return { ok: false, error };
        }
    }, [currentReservation, selectedDraaidag, updateJunctionStatus]);

    const handleSave = useCallback(async (newlySelectedMaterials, startDate, endDate) => {
        // Zorg ervoor dat we een array hebben
        const materialsArray = Array.isArray(newlySelectedMaterials) ? newlySelectedMaterials : [];
        const newMaterialIds = materialsArray.map(m => m.id);
        
        setIsSaving(true);
        
        // Optimistic update: update UI immediately
        setSelectedMaterials(materialsArray);
        if (typeof updateDraaidagLocally === 'function') {
            updateDraaidagLocally({ id: selectedDraaidag.id, fields: { AantalMateriaalGereserveerd: newMaterialIds.length } });
        }
        
        try {
            const response = await axios.post(`${process.env.REACT_APP_CURRENT_URL}/Api/save-reservation`, {
                reservationId: currentReservation?.id,
                draaidagId: selectedDraaidag.id,
                materialIds: newMaterialIds,
                startDate: startDate || selectedDraaidag.fields.Start || selectedDraaidag.fields.StartDatum,
                endDate: endDate || selectedDraaidag.fields.Eind || selectedDraaidag.fields.EindDatum,
                extraDagNa: extraDagNa,
                extraDagVoor: extraDagVoor
            });
            
            const extraDagenMsg = (extraDagVoor && extraDagNa) ? " (met extra dagen voor én na)" : 
                                  extraDagVoor ? " (met extra dag voor)" : 
                                  extraDagNa ? " (met extra dag na)" : "";
            toast.success(`Reservering opgeslagen${extraDagenMsg}!`);
            
            // Calculate actual dates for local state (same logic as backend)
            let actualStartDate = startDate || selectedDraaidag.fields.Start || selectedDraaidag.fields.StartDatum;
            let actualEndDate = endDate || selectedDraaidag.fields.Eind || selectedDraaidag.fields.EindDatum;
            
            if (extraDagVoor && actualStartDate) {
                const start = new Date(actualStartDate);
                start.setDate(start.getDate() - 1);
                actualStartDate = start.toISOString().split('T')[0];
            }
            if (extraDagNa && actualEndDate) {
                const end = new Date(actualEndDate);
                end.setDate(end.getDate() + 1);
                actualEndDate = end.toISOString().split('T')[0];
            }
            
            // Als er geen reservering was, hebben we er nu wel een
            if (!currentReservation && response.data.reservationId) {
                const newReservationMemo = {
                    id: response.data.reservationId,
                    fields: {
                        Start: actualStartDate,
                        Eind: actualEndDate,
                        Materiaal: newMaterialIds,
                        ExtraDagNa: extraDagNa,
                        ExtraDagVoor: extraDagVoor
                    }
                };
                setCurrentReservation(newReservationMemo);
            } else if (currentReservation && response.data.reservationId) {
                // Update existing reservation with new material data
                const updatedReservation = {
                    ...currentReservation,
                    fields: {
                        ...currentReservation.fields,
                        Materiaal: newMaterialIds,
                        Start: actualStartDate,
                        Eind: actualEndDate,
                        ExtraDagNa: extraDagNa,
                        ExtraDagVoor: extraDagVoor
                    }
                };
                setCurrentReservation(updatedReservation);
            }
            
            // Immediate refresh of junction data for newly added materials
            try {
                const reservationId = currentReservation?.id || response.data.reservationId;
                if (reservationId) {
                    const junctionResponse = await axios.get(
                        `${process.env.REACT_APP_CURRENT_URL}/Api/draaidag-data/${selectedDraaidag.id}?reservationId=${reservationId}`
                    );
                    const { junctionData: newJunctionData } = junctionResponse.data;
                    
                    // Update selected materials with new junction data
                    const updatedMaterials = materialsArray.map(material => {
                        const junction = newJunctionData.find(j => j.fields?.Materiaal?.[0] === material.id);
                        if (junction) {
                            return {
                                ...material,
                                fields: {
                                    ...material.fields,
                                    JunctionID: junction.id,
                                    JunctionStatus: junction.fields.StatusJunction || []
                                }
                            };
                        }
                        return material;
                    });
                    
                    setSelectedMaterials(updatedMaterials);
                    
                    // Also update alleMaterialen with junction data for consistency
                    const updatedAlleMaterialen = alleMaterialen.map(material => {
                        const junction = newJunctionData.find(j => j.fields?.Materiaal?.[0] === material.id);
                        if (junction) {
                            return {
                                ...material,
                                fields: {
                                    ...material.fields,
                                    JunctionID: junction.id,
                                    JunctionStatus: junction.fields.StatusJunction || []
                                }
                            };
                        }
                        return material;
                    });
                    setAlleMaterialen(updatedAlleMaterialen);
                }
            } catch (error) {
                console.error('Error refreshing junction data immediately:', error);
            }
            
            // Background sync voor data consistentie (niet blocking)
            // Dit zorgt ervoor dat andere gebruikers de wijzigingen zien
            setTimeout(() => { 
                fetchReservations(true); // true = force refresh, bypass cache
            }, 2000); // 2 seconde delay - genoeg tijd voor gebruiker om door te gaan
            
            if (typeof fetchDraaidagenFromBackend === 'function') {
                setTimeout(() => { 
                    fetchDraaidagenFromBackend(); 
                }, 2000);
            }
            
            // Immediate refresh of reservations to ensure data consistency
            setTimeout(() => {
                fetchReservations(true); // true = force refresh
            }, 500); // 500ms delay to ensure backend has processed the save
            
            setOpenWizard(false);
        } catch (err) {
            console.error('Save error:', err);
            
            // Revert optimistic updates on error
            if (currentReservation?.fields?.Materiaal) {
                // Revert to original materials if there was a previous reservation
                const originalMaterialIds = currentReservation.fields.Materiaal || [];
                const originalMaterials = alleMaterialen.filter(m => originalMaterialIds.includes(m.id));
                setSelectedMaterials(originalMaterials);
            } else {
                // Clear selection if this was a new reservation
                setSelectedMaterials([]);
            }
            
            if (typeof updateDraaidagLocally === 'function') {
                // Revert draaidag count
                const originalCount = currentReservation?.fields?.Materiaal?.length || 0;
                updateDraaidagLocally({ id: selectedDraaidag.id, fields: { AantalMateriaalGereserveerd: originalCount } });
            }
            
            if (err.response) {
                console.error('Response data:', err.response.data);
                console.error('Response status:', err.response.status);
                
                if (err.response.status === 409) {
                    // Conflict - materials were reserved by another user
                    toast.error(err.response.data.error?.message || 'Materialen zijn net gereserveerd door een andere gebruiker');
                    // Force refresh to show current availability
                    await fetchReservations(true);
                    const response = await axios.get(`${process.env.REACT_APP_CURRENT_URL}/Api/materialen`);
                    setAlleMaterialen(response.data);
                } else {
                    toast.error(`Opslaan mislukt: ${err.response.data.error?.message || 'Onbekende fout'}`);
                }
        } else {
                toast.error("Opslaan mislukt.");
            }
        } finally {
            setIsSaving(false);
        }
    }, [currentReservation, selectedDraaidag, fetchReservations, updateDraaidagLocally, setSelectedDraaidag, setCurrentReservation, alleMaterialen, fetchDraaidagenFromBackend, extraDagNa]);
    
    const handleGoBack = useCallback(() => setSelectedDraaidag(null), [setSelectedDraaidag]);
    const handleOpenWizard = useCallback(() => {
        setSelectedCategoryIndex(null); // Reset category when opening normally
        setOpenWizard(true);
    }, []);
    const handleCloseWizard = useCallback(() => {
        setOpenWizard(false);
        // Delay resetting category to prevent flicker
        setTimeout(() => {
            setSelectedCategoryIndex(null);
        }, 300);
    }, []);

    // Sync notitie als reservation wisselt
    useEffect(() => {
        const notitieValue = currentReservation?.fields?.Notities || '';
        
        // Don't overwrite the notitie if it's currently being edited
        if (notitieStatus === 'saving') {
            return;
        }
        
        if (notitie !== notitieValue) {
            setNotitie(notitieValue);
            lastSavedValue.current = notitieValue;
        }
    }, [currentReservation]);

    // Sync extraDagNa en extraDagVoor als reservation wisselt
    useEffect(() => {
        const extraDagNaValue = currentReservation?.fields?.ExtraDagNa === true;
        const extraDagVoorValue = currentReservation?.fields?.ExtraDagVoor === true;
        setExtraDagNa(extraDagNaValue);
        setExtraDagVoor(extraDagVoorValue);
    }, [currentReservation]);

    // PATCH notitie naar Airtable - manual save
    const saveNotitie = useCallback(async (value) => {
        if (!currentReservation?.id) return;
        
        setNotitieStatus('saving');
        
        try {
            await axios.patch(`${process.env.REACT_APP_CURRENT_URL}/Api/reservations/${currentReservation.id}/notitie`, {
                notitie: value
            });
            
            setNotitieStatus('saved');
            setTimeout(() => setNotitieStatus(''), 800);
            
            // Manually update the reservation in the context
            updateReservationField(currentReservation.id, 'Notities', value);

        } catch (e) {
            setNotitieStatus('error');
            setTimeout(() => setNotitieStatus(''), 1500);
            toast.error('Fout bij opslaan notitie');
        }
    }, [currentReservation, updateReservationField]);

    // Manual save handlers
    const handleNotitieSave = useCallback(() => {
        if (notitie !== (currentReservation?.fields?.Notities || '')) {
            saveNotitie(notitie);
        }
    }, [notitie, saveNotitie, currentReservation]);

    const handleNotitieKeyPress = useCallback((e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault();
            handleNotitieSave();
        }
    }, [handleNotitieSave]);

    // Add delete reservation functionality
    const handleDeleteReservation = useCallback(async (reservationId) => {
        if (!reservationId) return;
        
        try {
            
            // Use backend endpoint instead of direct Airtable API calls
            const response = await axios.delete(`${process.env.REACT_APP_CURRENT_URL}/Api/reservations/${reservationId}`);

            if (response.status === 200) {
                toast.success("Reservering succesvol verwijderd!");
                
                // Clear all local state
                setCurrentReservation(null);
                setSelectedMaterials([]);
                setAlleMaterialen([]);
                setNotitie('');
                
                // Refresh all data
                await fetchReservations(true);
                
                // If we have a selected draaidag, reload the material data
                if (selectedDraaidag) {
                    await refreshMaterialData();
                }
                
                setSelectedDraaidag(null); // Go back to calendar view
            } else {
                toast.error('Fout bij verwijderen van de reservering');
            }
        } catch (error) {
            console.error("Error during delete operation: ", error);
            if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
                toast.error(`Fout bij verwijderen van reservering: ${error.response.data?.error || 'Onbekende fout'}`);
            } else {
                toast.error("Fout bij verwijderen van reservering");
            }
        }
    }, [fetchReservations, setSelectedDraaidag, setCurrentReservation, selectedDraaidag, refreshMaterialData]);

    // Functie om de "Geen Materiaal Nodig" checkbox te behandelen
    const handleGeenMateriaalNodigChange = async (event) => {
        const isChecked = event.target.checked;
        setGeenMateriaalNodig(isChecked); // Optimistic update
        setOpslaanStatus('Opslaan...');
        setIsSaving(true);

        try {
            const patchData = { 'Geen Materiaal Nodig': isChecked };
            // Use backend endpoint instead of direct Airtable call (security)
            await axios.patch(`${process.env.REACT_APP_CURRENT_URL}/Api/draaidagen/${selectedDraaidag.id}`, { fields: patchData });
            
            // Update de lokale draaidag state in DraaidagenOverzicht
            if (updateDraaidagLocally) {
                const updatedDraaidag = {
                    ...selectedDraaidag,
                    fields: {
                        ...selectedDraaidag.fields,
                        'Geen Materiaal Nodig': isChecked
                    }
                };
                updateDraaidagLocally(updatedDraaidag);
            }
            
            toast.success('Wijziging opgeslagen!');
            setOpslaanStatus('Opgeslagen');

        } catch (error) {
            console.error('Fout bij bijwerken "Geen materiaal nodig":', error);
            toast.error('Fout bij opslaan wijziging.');
            setGeenMateriaalNodig(!isChecked); // Revert optimistic update on error
            setOpslaanStatus('Fout');
        } finally {
            setIsSaving(false);
            setTimeout(() => setOpslaanStatus(''), 2000);
        }
    };

    // Handler voor "Extra dag na draaidag" checkbox - slaat direct op in Airtable
    const handleExtraDagNaChange = async (event) => {
        const isChecked = event.target.checked;
        
        // Alleen beschikbaar als er een reservering is
        if (!currentReservation?.id) {
            setExtraDagNa(isChecked); // Alleen lokale state updaten, wordt opgeslagen bij eerste reservering
            return;
        }
        
        setExtraDagNa(isChecked); // Optimistic update
        setIsSaving(true);
        
        try {
            // Bereken de nieuwe einddatum
            const baseDraaidagEind = selectedDraaidag.fields?.Eind || selectedDraaidag.fields?.EindDatum;
            let newEndDate = baseDraaidagEind;
            
            if (isChecked && baseDraaidagEind) {
                const end = new Date(baseDraaidagEind);
                end.setDate(end.getDate() + 1);
                newEndDate = end.toISOString().split('T')[0];
            }
            
            // Update reservering in Airtable via backend
            await axios.patch(`${process.env.REACT_APP_CURRENT_URL}/Api/reservations/${currentReservation.id}`, {
                fields: {
                    ExtraDagNa: isChecked,
                    Eind: newEndDate
                }
            });
            
            // Update lokale state
            const updatedReservation = {
                ...currentReservation,
                fields: {
                    ...currentReservation.fields,
                    ExtraDagNa: isChecked,
                    Eind: newEndDate
                }
            };
            setCurrentReservation(updatedReservation);
            
            // Ververs de reserveringen cache zodat de waarde persistent blijft bij navigatie
            await fetchReservations(true);
            
            toast.success(isChecked ? 'Extra dag toegevoegd aan reservering!' : 'Extra dag verwijderd van reservering.');
        } catch (error) {
            console.error('Fout bij bijwerken "Extra dag na draaidag":', error);
            toast.error('Fout bij opslaan wijziging.');
            setExtraDagNa(!isChecked); // Revert optimistic update on error
        } finally {
            setIsSaving(false);
        }
    };

    // Handler voor "Extra dag voor draaidag" checkbox - slaat direct op in Airtable
    const handleExtraDagVoorChange = async (event) => {
        const isChecked = event.target.checked;
        
        // Alleen beschikbaar als er een reservering is
        if (!currentReservation?.id) {
            setExtraDagVoor(isChecked); // Alleen lokale state updaten, wordt opgeslagen bij eerste reservering
            return;
        }
        
        setExtraDagVoor(isChecked); // Optimistic update
        setIsSaving(true);
        
        try {
            // Bereken de nieuwe startdatum
            const baseDraaidagStart = selectedDraaidag.fields?.Start || selectedDraaidag.fields?.StartDatum;
            let newStartDate = baseDraaidagStart;
            
            if (isChecked && baseDraaidagStart) {
                const start = new Date(baseDraaidagStart);
                start.setDate(start.getDate() - 1);
                newStartDate = start.toISOString().split('T')[0];
            }
            
            // Update reservering in Airtable via backend
            await axios.patch(`${process.env.REACT_APP_CURRENT_URL}/Api/reservations/${currentReservation.id}`, {
                fields: {
                    ExtraDagVoor: isChecked,
                    Start: newStartDate
                }
            });
            
            // Update lokale state
            const updatedReservation = {
                ...currentReservation,
                fields: {
                    ...currentReservation.fields,
                    ExtraDagVoor: isChecked,
                    Start: newStartDate
                }
            };
            setCurrentReservation(updatedReservation);
            
            // Ververs de reserveringen cache zodat de waarde persistent blijft bij navigatie
            await fetchReservations(true);
            
            toast.success(isChecked ? 'Extra dag voor draaidag toegevoegd!' : 'Extra dag voor draaidag verwijderd.');
        } catch (error) {
            console.error('Fout bij bijwerken "Extra dag voor draaidag":', error);
            toast.error('Fout bij opslaan wijziging.');
            setExtraDagVoor(!isChecked); // Revert optimistic update on error
        } finally {
            setIsSaving(false);
        }
    };

    // Note: This useEffect handles the scenario where the reservation is deleted.
    // Only clear state if we previously had a currentReservation but it is no longer present in reservations.
    useEffect(() => {
        if (selectedDraaidag && Array.isArray(reservations) && currentReservation) {
            // First try ID match
            let found = reservations.find(r => r?.fields?.IDdraaidagOutlook?.[0] === selectedDraaidag.id);

            // Fallback: date match to avoid false negatives when ID link is missing
            if (!found) {
                const selStart = selectedDraaidag.fields?.Start || selectedDraaidag.fields?.StartDatum;
                const selEind = selectedDraaidag.fields?.Eind || selectedDraaidag.fields?.EindDatum;
                if (selStart && selEind) {
                    found = reservations.find(r => r?.fields?.Start === selStart && r?.fields?.Eind === selEind);
                }
            }

            if (!found) {
                setCurrentReservation(null);
                setSelectedMaterials([]);
                setNotitie('');
                setGeenMateriaalNodig(false);
                refreshMaterialData();
            }
        }
    }, [reservations, selectedDraaidag, currentReservation, setCurrentReservation, refreshMaterialData]);

    // Preload functie voor snellere response
    const preloadData = useCallback(async () => {
        try {
            // Preload materialen in de achtergrond
            const response = await axios.get(`${process.env.REACT_APP_CURRENT_URL}/Api/materialen`);
            setMaterialsCache({ data: response.data, timestamp: Date.now() });
        } catch (error) {
            // Preload failed (non-critical)
        }
    }, []);

    // Preload data bij component mount
    useEffect(() => {
        preloadData();
    }, [preloadData]);

    // Periodic sync voor data consistentie (elke 30 seconden)
    useEffect(() => {
        if (!selectedDraaidag) return;
        
        const syncInterval = setInterval(() => {
            // Silent sync - geen loading state, alleen data update
            fetchReservations(true);
        }, 30000); // 30 seconden
        
        return () => clearInterval(syncInterval);
    }, [selectedDraaidag, fetchReservations]);

    // In de component, voeg de handler toe voor het kopiëren:
    const handleCopyMaterials = useCallback(async (materials) => {
        try {
            
            // Filter materialen die al in de huidige selectie zitten
            const existingMaterialIds = new Set(selectedMaterials.map(m => m.id));
            const newMaterials = materials.filter(m => !existingMaterialIds.has(m.id));
            
            if (newMaterials.length === 0) {
                toast.info('Alle geselecteerde materialen zijn al toegevoegd');
                setCopyDialogOpen(false);
                return;
            }
            
            // Voeg de nieuwe materialen toe aan de huidige selectie
            const updatedMaterials = [...selectedMaterials, ...newMaterials];
            
            // Sla de reservering op met de nieuwe materialen
            await handleSave(updatedMaterials);
            
            toast.success(`${newMaterials.length} nieuwe materialen succesvol gekopieerd!`);
            setCopyDialogOpen(false);
        } catch (error) {
            console.error('Error copying materials:', error);
            toast.error('Fout bij kopiëren van materialen');
        }
    }, [selectedMaterials, handleSave]);

    const handleSaveAsSet = async () => {
        if (!newSetName.trim()) {
            toast.error('Geef de materiaalset een naam.');
            return;
        }

        const materialIds = selectedMaterials.map(m => m.id);
        if (materialIds.length === 0) {
            toast.error('Er zijn geen materialen in deze reservering om op te slaan als set.');
            return;
        }

        try {
            await axios.post(`${process.env.REACT_APP_CURRENT_URL}/api/materiaalsets`, {
                name: newSetName,
                materialIds: materialIds
            });
            toast.success(`Materiaalset "${newSetName}" succesvol aangemaakt!`);
            setSaveAsSetDialogOpen(false);
            setNewSetName('');
            // Optional: refresh the list of materiaalsets
            fetchMateriaalsets();
        } catch (error) {
            console.error('Error creating material set:', error);
            toast.error('Fout bij het aanmaken van de materiaalset.');
        }
    };

    if (error) return <Typography color="error">Fout: {error}</Typography>;
    if (!selectedDraaidag) return <Typography>Selecteer een draaidag.</Typography>;

  return (
        <Grid container justifyContent="center">
            <Paper style={{ padding: '20px', margin: '20px auto', width: '90%', maxWidth: '1200px' }}>
                <Button onClick={handleGoBack} size="small" sx={{ mb: 1 }}>
                    &larr; Terug naar kalender
                </Button>

                {/* Optimistic Preview - toon direct */}
                <DraaidagPreview />
                
                <Grid container spacing={1} alignItems="center" justifyContent="space-between" sx={{ padding: { xs: '8px 12px', sm: '8px 24px' }, marginBottom: '16px' }}>
                    <Grid item xs={12}>
                        <Stack 
                            direction={{ xs: 'column', sm: 'row' }} 
                            spacing={2} 
                            alignItems={{ xs: 'stretch', sm: 'center' }} 
                            justifyContent="space-between"
                        >
                            {/* Linker groep: Primaire acties */}
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                                <Tooltip title={currentReservation ? "Open de wizard om materialen toe te voegen of te verwijderen" : "Maak een nieuwe materiaalreservering voor deze draaidag"} arrow>
                                    <span>
                                        <Button variant="contained" color="primary" size="small" onClick={handleOpenWizard} disabled={isSaving}>
                                            {isSaving ? "Opslaan..." : (currentReservation ? "Wijzig reservering" : "Maak reservering")}
                                        </Button>
                                    </span>
                                </Tooltip>
                                {currentReservation && (
                                    <Tooltip title="Kopieer de materiaallijst van een andere draaidag naar deze reservering" arrow>
                                        <Button variant="outlined" color="secondary" size="small" onClick={() => setCopyDialogOpen(true)}>
                                            Kopieer andere draaidag
                                        </Button>
                                    </Tooltip>
                                )}
                                {currentReservation && selectedMaterials.length > 0 && (
                                    <Tooltip title="Sla de huidige materiaalcombinatie op als herbruikbare set" arrow>
                                        <Button variant="outlined" color="info" size="small" onClick={() => setSaveAsSetDialogOpen(true)}>
                                            Opslaan als set
                                        </Button>
                                    </Tooltip>
                                )}
                                <Tooltip title="Materialen worden 1 dag eerder gereserveerd (voor voorbereiding)" arrow>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={extraDagVoor}
                                                onChange={handleExtraDagVoorChange}
                                                size="small"
                                                color="info"
                                                disabled={isSaving}
                                            />
                                        }
                                        label="-1📅"
                                        sx={{ ml: 1 }}
                                    />
                                </Tooltip>
                                <Tooltip title="Materialen worden 1 dag langer gereserveerd (voor late terugkomst)" arrow>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={extraDagNa}
                                                onChange={handleExtraDagNaChange}
                                                size="small"
                                                color="warning"
                                                disabled={isSaving}
                                            />
                                        }
                                        label="📅+1"
                                        sx={{ ml: 1 }}
                                    />
                                </Tooltip>
                            </Stack>
                            
                            {/* Rechter groep: Destructieve actie */}
                            {currentReservation && (
                                <Tooltip title="Verwijder deze materiaalreservering permanent (kan niet ongedaan gemaakt worden)" arrow>
                                    <Button 
                                        variant="outlined" 
                                        color="error" 
                                        size="small" 
                                        onClick={() => setDeleteDialogOpen(true)}
                                        sx={{ mt: { xs: 1, sm: 0 } }}
                                    >
                                        Verwijder
                                    </Button>
                                </Tooltip>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
                
                {/* Compacte notitie sectie */}
                {currentReservation && (
                    <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="Notitie"
                                value={notitie}
                                onChange={e => setNotitie(e.target.value)}
                                onBlur={handleNotitieSave}
                                onKeyPress={handleNotitieKeyPress}
                                multiline
                                minRows={1}
                                fullWidth
                                size="small"
                                variant="outlined" 
                                placeholder="Type hier je notitie (Ctrl+Enter om op te slaan, of klik buiten het veld)"
                                InputProps={{
                                    endAdornment: notitieStatus === 'saving' ? '⏳' : notitieStatus === 'saved' ? '✅' : notitieStatus === 'error' ? '❌' : null
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            borderColor: notitieStatus === 'saving' ? '#ff9800' : notitieStatus === 'error' ? '#f44336' : '#1976d2'
                                        },
                                        '&.Mui-focused': {
                                            borderColor: notitieStatus === 'saving' ? '#ff9800' : notitieStatus === 'error' ? '#f44336' : '#1976d2'
                                        }
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: notitieStatus === 'saving' ? '#ff9800' : notitieStatus === 'error' ? '#f44336' : undefined
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                )}

                <Snackbar
                    open={notitieStatus === 'error'}
                    autoHideDuration={2000}
                    message="Fout bij opslaan notitie"
                    onClose={() => setNotitieStatus('')}
                />

                {/* Toon melding als er nog geen reservering is */}
                {!currentReservation && (
                    <div style={{
                        margin: '32px 0',
                        padding: '24px',
                        background: '#fafafa',
                        borderRadius: '8px',
                        textAlign: 'center',
                        fontSize: '1.2rem',
                        color: '#888'
                    }}>
                        Nog geen reservering aangemaakt......
                    </div>
                )}

                {/* Toon "Geen Materiaal Nodig" checkbox als:
                    - Er geen reservering is, OF
                    - Er een reservering is maar zonder materialen */}
                {(!currentReservation || selectedMaterials.length === 0) && (
                    <Grid container alignItems="center" spacing={2} sx={{ marginTop: 2, marginBottom: 2 }}>
                        <Grid item>
                            <input
                                type="checkbox"
                                id="geen-materiaal-nodig"
                                checked={geenMateriaalNodig}
                                onChange={handleGeenMateriaalNodigChange}
                            />
                        </Grid>
                        <Grid item>
                            <label htmlFor="geen-materiaal-nodig">Geen materiaal nodig</label>
                            {opslaanStatus === 'saving' && <span style={{ marginLeft: 8 }}>⏳</span>}
                            {opslaanStatus === 'saved' && <span style={{ marginLeft: 8 }}>✅</span>}
                            {opslaanStatus === 'error' && <span style={{ marginLeft: 8, color: 'red' }}>❌</span>}
                        </Grid>
                    </Grid>
                )}

                {/* ReserveringsData component voor datum-wijzig functionaliteit */}
                {currentReservation && (
            <ReserveringsData
                notitie={notitie}
                selectedMaterials={selectedMaterials}
                setSelectedMaterials={setSelectedMaterials}                
                        reservedMaterials={selectedMaterials}
                        geselecteerdeDraaidag={selectedDraaidag}
                        currentReservation={currentReservation}
                        reservations={processReservationsData(reservations)}
                        postOrUpdateReservering={handleSave}
                    />
                )}

                {/* Loading skeleton voor materialen */}
                <GereserveerdMateriaalLijst 
                    reservedMaterials={selectedMaterials} 
                    setOpenWizard={setOpenWizard}
                    updateJunctionStatus={updateJunctionStatusWithLegacySupport}
                    setCurrentStepCategorie={(index) => {
                        setSelectedCategoryIndex(index);
                        setOpenWizard(true);
                    }}
                    currentStepCategorie={0}
                    handleOpenScanner={() => {}}
                />

                <SimpleMaterialWizard
                    openWizard={openWizard}
                    handleCloseWizard={handleCloseWizard}
                    materialen={alleMaterialen}
                    selectedMaterials={selectedMaterials}
                    setSelectedMaterials={setSelectedMaterials}
                    onSave={handleSave}
                    currentReservation={currentReservationMemo}
                    geselecteerdeDraaidag={selectedDraaidag}
                    reservationsData={processReservationsData(reservations)}
                    reservations={reservations}
                    initialCategory={selectedCategoryIndex}
                    materiaalsets={materiaalsets}
                />

                {/* Update de CopyMaterialsDialog met de juiste props */}
                <CopyMaterialsDialog 
                    open={copyDialogOpen} 
                    onClose={() => setCopyDialogOpen(false)}
                    currentDraaidag={{
                        ...selectedDraaidag,
                        currentReservationId: currentReservation?.id
                    }}
                    onCopy={handleCopyMaterials}
                    reservations={reservations}
                />

                <Dialog open={saveAsSetDialogOpen} onClose={() => setSaveAsSetDialogOpen(false)}>
                    <DialogTitle>Opslaan als Materiaalset</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Voer een naam in voor deze nieuwe materiaalset. De set zal alle
                            {selectedMaterials.length} materialen van de huidige reservering bevatten.
                        </DialogContentText>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="name"
                            label="Naam van de set"
                            type="text"
                            fullWidth
                            variant="standard"
                            value={newSetName}
                            onChange={(e) => setNewSetName(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSaveAsSet()}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setSaveAsSetDialogOpen(false)}>Annuleren</Button>
                        <Button onClick={handleSaveAsSet} variant="contained">Opslaan</Button>
                    </DialogActions>
                </Dialog>

                {/* Bevestigingsdialog voor verwijderen */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle>Reservering verwijderen</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Weet je zeker dat je deze reservering wilt verwijderen? 
                            Dit kan niet ongedaan worden gemaakt.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>Annuleren</Button>
                        <Button 
                            onClick={() => {
                                handleDeleteReservation(currentReservation.id);
                                setDeleteDialogOpen(false);
                            }} 
                            variant="contained" 
                            color="error"
                        >
                            Verwijderen
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
    </Grid>
  );
});